import React, { useEffect, useMemo, useState } from 'react';

import pluralize from 'pluralize';

import { FormatHelper } from '@evinced-private/ui-common';

import { IPropertyViewContext, usePropertyViewContext } from 'src/providers/PropertyViewProvider';
import { getScanOverview, ScanOverviewResponse } from 'src/services/scan/ScanService';
import { PagesOverviewResponse } from 'src/types/OverviewTypes';

import { RoutesHelper } from '../../helpers/RoutesHelper';
import { logger } from '../../services/Logger';
import { EvLinkLocal } from '../common/ev-link-local/EvLinkLocal';

import './ScanSummaryTags.scss';

const LINK_SEPARATOR = '|';

interface ScanSummaryTagsProps {
	propertyId: string;
	scanId: string;
}

interface SummaryLinkItem {
	key: string;
	linkText: string;
}

const ScanSummaryTags = ({ propertyId, scanId }: ScanSummaryTagsProps): JSX.Element => {
	const [pagesOverview, setPagesOverview] = useState<PagesOverviewResponse>(null);
	const propertyViewContext: IPropertyViewContext = usePropertyViewContext();

	const viewId = useMemo(() => propertyViewContext?.currentView?.id, [propertyViewContext]);

	useEffect(() => {
		const loadScanOverview = async (): Promise<void> => {
			try {
				const scanOverview: ScanOverviewResponse = await getScanOverview(scanId, viewId);
				setPagesOverview(scanOverview.pages);
			} catch (error) {
				setPagesOverview(null);
				logger.error('Error occured while getting scan data', error);
			}
		};
		loadScanOverview();
	}, [scanId, viewId]);

	const summaryLinks: SummaryLinkItem[] = useMemo(() => {
		if (!pagesOverview) {
			return [];
		}
		return [
			{
				key: 'scanned',
				linkText: `${FormatHelper.formatNumber(pagesOverview.overview.scanned)} scanned`
			},
			{
				key: 'duplicates',
				linkText: `${FormatHelper.formatNumber(pagesOverview.overview.filteredOut)} ${pluralize(
					'duplicate',
					pagesOverview.overview.filteredOut
				)}`
			},
			{
				key: 'failed',
				linkText: `${FormatHelper.formatNumber(pagesOverview.overview.failed)} failed`
			}
		];
	}, [pagesOverview]);

	return pagesOverview ? (
		<div className="header-summary-pane" aria-label="scanned pages summary">
			<p>Scanned pages:</p>
			{summaryLinks.map((link, index) => (
				<React.Fragment key={link.key}>
					{index > 0 && <span className="link-separator">{LINK_SEPARATOR}</span>}
					<EvLinkLocal
						url={RoutesHelper.getScanResultUrlsStatusPath(propertyId, scanId)}
						linkText={link.linkText}
						className="summary-link"
					/>
				</React.Fragment>
			))}
		</div>
	) : null;
};

export { ScanSummaryTags };
